/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Leaf, Baby, HeartStraight, Recycle, Users, GraduationCap,
  ArrowRight, Globe, Tree, Drop, Plant, Thermometer,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

export default function CSRSustainability() {
  useEffect(() => {
    document.title = "CSR & Sustainability · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="page-csr">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-b from-[#F0F5EE] to-white">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 80% 30%, rgba(45,90,39,0.12) 0%, transparent 45%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow>CSR & Sustainability</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 leading-[1.05] text-slate-900">
              Driving <span className="italic text-[#2D5A27]">circular-waste</span> innovation for public health.
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-xl leading-relaxed">
              Our work touches the intersection of environment, women's health,
              child welfare, and urban sanitation. Every program is measured,
              reported, and UN-SDG-aligned.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/#contact" data-testid="csr-cta-partner" className={BTN_GREEN}>
                Partner for CSR programs <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link to="/white-papers" data-testid="csr-cta-reports"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 text-sm font-medium">
                Download impact reports
              </Link>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <div className="grid grid-cols-2 gap-3">
              <ImpactKPI Icon={Leaf} label="Waste diverted" v="14,280 t" sub="YTD" color="#2D5A27"/>
              <ImpactKPI Icon={Thermometer} label="CO₂ saved" v="8,412 t" sub="equivalent" color="#0A2540"/>
              <ImpactKPI Icon={Users} label="Communities impacted" v="38" sub="jurisdictions" color="#2D5A27"/>
              <ImpactKPI Icon={GraduationCap} label="Citizens educated" v="120K+" sub="awareness drive" color="#0A2540"/>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SDG ALIGNMENT */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow>UN SDG Alignment</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Every program mapped to UN Sustainable Development Goals.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-12">
            <SDG n="3" title="Good Health & Well-being" color="#2E7D32"/>
            <SDG n="5" title="Gender Equality" color="#E5243B"/>
            <SDG n="6" title="Clean Water & Sanitation" color="#26BDE2"/>
            <SDG n="11" title="Sustainable Cities" color="#FD9D24"/>
            <SDG n="12" title="Responsible Consumption" color="#BF8B2E"/>
            <SDG n="13" title="Climate Action" color="#3F7E44"/>
          </div>
        </div>
      </section>

      {/* FLAGSHIP PROGRAMS */}
      <section className="py-24 bg-[#F7F6F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow>Flagship programs</Eyebrow>
          <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">Programs that compound impact.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <FeatureCard Icon={HeartStraight} title="Women's Menstrual Hygiene" desc="Private, non-stigmatized sanitary-napkin disposal across apartments, schools & slum colonies. 4,800+ women served in Trivandrum."/>
            <FeatureCard Icon={Baby} title="Diaper Waste Reduction" desc="Special segregation + authorized disposal routes for diapers in apartments and nurseries. Prevents drain blockage + vector disease."/>
            <FeatureCard Icon={Recycle} title="Circular Recycling Co-ops" desc="Women-led ward-level recycling cooperatives — formalized, trained, and fairly paid under Rescigo's revenue-share model."/>
            <FeatureCard Icon={Tree} title="Urban Tree Offsets" desc="1 tree planted per 500 kg of organic waste composted. 14,200+ saplings across 38 panchayats in 2025."/>
            <FeatureCard Icon={Drop} title="Public Sanitation Drives" desc="Monthly deep-clean drives in high-density colonies — Rescigo runs the logistics, LSGDs contribute manpower, citizens participate."/>
            <FeatureCard Icon={GraduationCap} title="Public Health Education" desc="Rescigo Academy's free 'Home Hygiene 101' course is licensed to 41 schools and 18 LSGD campaigns at zero cost."/>
          </div>
        </div>
      </section>

      {/* ANNUAL IMPACT REPORT */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow>Annual Impact Report · 2025</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">Transparent. Measured. Third-party verified.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Our impact reports are audited annually by KPMG (Sustainability Assurance)
              and released in a public-facing, machine-readable format. Download past
              reports or request a briefing with our sustainability council.
            </p>
            <div className="mt-6"><BulletList items={[
              "KPMG-assured sustainability disclosures",
              "GRI 306 · BRSR · SASB aligned",
              "Third-party verified waste-diversion & CO₂ claims",
              "Open-source methodology for peer review",
            ]}/></div>
          </div>
          <div className="bg-gradient-to-br from-[#2D5A27] to-[#1A3317] text-white rounded-2xl p-10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 50% 0%, white 0%, transparent 50%)" }}/>
            <div className="relative">
              <Plant size={36} weight="duotone" className="opacity-80"/>
              <div className="font-heading text-3xl mt-5 leading-tight">
                "Rescigo turned our CSR budget from cost-center into a circular revenue engine."
              </div>
              <div className="mt-5 text-sm text-white/70">
                — Head of ESG, multinational manufacturing firm
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="CSR Partnerships"
        title="Put your CSR budget where impact is measured."
        body="We partner with corporates, foundations, and philanthropies on scoped programs with quarterly reporting and third-party verification."
        primary={{ label: "Partner for CSR programs", to: "/#contact", testid: "csr-cta-bottom" }}
        secondary={{ label: "White papers", to: "/white-papers" }}
      />
      <MarketingFooter/>
    </div>
  );
}

const ImpactKPI = ({ Icon, label, v, sub, color }) => (
  <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-5">
    <Icon size={18} weight="duotone" style={{ color }}/>
    <div className="font-heading text-3xl mt-3" style={{ color }}>{v}</div>
    <div className="text-xs text-slate-700 font-medium mt-1">{label}</div>
    <div className="text-[10px] text-slate-500">{sub}</div>
  </div>
);

const SDG = ({ n, title, color }) => (
  <div className="bg-white rounded-xl ring-1 ring-slate-200 p-5 flex items-center gap-4 hover:shadow-md transition">
    <div className="w-14 h-14 rounded-lg grid place-items-center text-white font-heading text-2xl font-bold flex-none" style={{ background: color }}>
      {n}
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">SDG {n}</div>
      <div className="font-medium text-slate-900 text-sm mt-0.5">{title}</div>
    </div>
  </div>
);
